import { useQuery } from '@tanstack/react-query';
import env from '../config/env';
import { driverApi } from '../services/driverApi';
import { isBelowMinimum } from '../lib/appVersion';
import {
  hasV1Bridge,
  legacyPlatform,
  native,
  type NativePlatform,
} from '../lib/nativeBridge';

export interface UpdateRequired {
  /** "the App Store" or "Google Play", for the screen's sentence. */
  store: string;
  /** Where the update is. */
  url: string;
}

const storeFor = (platform: NativePlatform): UpdateRequired =>
  platform === 'ios'
    ? { store: 'the App Store', url: env.appStore.ios }
    : { store: 'Google Play', url: env.appStore.android };

/**
 * The store to send this build to when the server's minimum version is above
 * it, or null. A browser is never stopped; nor is any build while the answer
 * is loading or failed, since a flaky network must not lock drivers out of
 * their calls.
 *
 * A v2 wrapper reports its version. One built before v2 reports none, and
 * every such build is older than any minimum the server could set, since v2
 * is what the minimum exists to require: so it is stopped once its platform
 * has a minimum at all. Checked only on the v2 version, those builds were
 * never stopped, and the old shell they keep could not have been deleted.
 */
export const useUpdateRequired = (): UpdateRequired | null => {
  const relay = native();
  const legacy = !relay && hasV1Bridge();
  const { data } = useQuery({
    queryKey: ['app-config'],
    queryFn: () => driverApi.getAppConfig(),
    enabled: relay !== null || legacy,
    staleTime: 60 * 60 * 1000,
  });

  if (!data) return null;
  if (relay) {
    return isBelowMinimum(relay.platform, relay.appVersion, data.minimumVersion)
      ? storeFor(relay.platform)
      : null;
  }
  if (legacy) {
    const platform = legacyPlatform();
    return data.minimumVersion[platform] ? storeFor(platform) : null;
  }
  return null;
};
